"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Compass, ShieldCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function DeliveryPlate() {
  const shouldReduceMotion = useReducedMotion();
  const plateMotionVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="deliveryplanningplatecta" data-nav="light" className="w-full bg-background py-16 md:py-24 text-foreground transition-colors">
      <div className="container mx-auto px-4 max-w-5xl">
        <motion.div variants={plateMotionVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="relative bg-card border border-border p-1 shadow-xs">
          {/* 4px Inset Margin Frame with Inner Hairline Border */}
          <div className="relative border border-border/80 bg-background/50 p-6 sm:p-10 md:p-14">
            {/* Corner Registration Ticks (6px Perpendicular Datum Marks) */}
            {/* Top-Left */}
            <div className="absolute -top-[1px] -left-[1px] w-[6px] h-[6px] border-t-2 border-l-2 border-foreground pointer-events-none" aria-hidden="true" />
            {/* Top-Right */}
            <div className="absolute -top-[1px] -right-[1px] w-[6px] h-[6px] border-t-2 border-r-2 border-foreground pointer-events-none" aria-hidden="true" />
            {/* Bottom-Left */}
            <div className="absolute -bottom-[1px] -left-[1px] w-[6px] h-[6px] border-b-2 border-l-2 border-foreground pointer-events-none" aria-hidden="true" />
            {/* Bottom-Right */}
            <div className="absolute -bottom-[1px] -right-[1px] w-[6px] h-[6px] border-b-2 border-r-2 border-foreground pointer-events-none" aria-hidden="true" />

            {/* Industrial Specification Header / Eyebrow */}
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/60 pb-4 mb-6 md:mb-8">
              <div className="inline-flex items-center gap-2">
                <Compass className="size-4 text-primary" aria-hidden="true" />
                <span className="font-mono text-xs font-semibold tracking-wider text-muted-foreground uppercase">
                  Logistics &amp; Pre-Order Advisory
                </span>
              </div>
              <div className="flex items-center gap-1 font-mono text-[11px] text-muted-foreground/80 tracking-widest uppercase">
                <ShieldCheck className="size-3.5 text-foreground" aria-hidden="true" />
                <span>Commercial Spec Verification</span>
              </div>
            </div>

            {/* Rating Plate Headline */}
            <div className="max-w-3xl mb-6">
              <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight uppercase text-foreground leading-[1.05]">
                Plan Your Mechanical Bull Delivery Before You Order
              </h2>
            </div>

            {/* Dictated Technical Copy */}
            <div className="max-w-3xl space-y-3 mb-8 md:mb-10 text-muted-foreground text-sm sm:text-base leading-relaxed">
              <p>
                The exact machine, equipment package and destination all affect
                the delivery process. Review the product first, then provide
                the destination information needed to confirm the available
                shipping arrangement.
              </p>
              <p>
                If you already have a bull in mind, include its product name or
                SKU when contacting us so your question relates to the correct
                machine.
              </p>
            </div>

            {/* Button Actions Grid */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-3 pt-2">
              <motion.div whileTap={shouldReduceMotion ? {} : {
              scale: 0.99
            }} transition={{
              duration: 0.08
            }} className="flex-1 sm:flex-none">
                <Button asChild className="w-full sm:w-auto font-serif uppercase tracking-wider font-semibold text-base h-11 px-6 rounded-none bg-primary text-primary-foreground hover:bg-primary/90 shadow-none">
                  <Link to="/NewMechanicalBullsPage">
                    <span>View New Bulls</span>
                    <ArrowRight className="size-4 ml-1.5" aria-hidden="true" />
                  </Link>
                </Button>
              </motion.div>

              <motion.div whileTap={shouldReduceMotion ? {} : {
              scale: 0.99
            }} transition={{
              duration: 0.08
            }} className="flex-1 sm:flex-none">
                <Button asChild variant="outline" className="w-full sm:w-auto font-serif uppercase tracking-wider font-semibold text-base h-11 px-6 rounded-none border-foreground/30 bg-transparent text-foreground hover:bg-muted shadow-none">
                  <Link to="/UsedMechanicalBullsPage">
                    View Used Bulls
                  </Link>
                </Button>
              </motion.div>

              <motion.div whileTap={shouldReduceMotion ? {} : {
              scale: 0.99
            }} transition={{
              duration: 0.08
            }} className="flex-1 sm:flex-none">
                <Button asChild variant="secondary" className="w-full sm:w-auto font-serif uppercase tracking-wider font-semibold text-base h-11 px-6 rounded-none bg-secondary text-secondary-foreground hover:bg-secondary/90 border border-border shadow-none">
                  <Link to="/ContactPage">
                    Ask About Shipping
                  </Link>
                </Button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}